/**
 * CSRF Interceptor
 *
 * Adds CSRF (Cross-Site Request Forgery) tokens to state-changing HTTP requests.
 * Protects against CSRF attacks by ensuring requests originate from the application.
 *
 * The interceptor:
 * - Adds CSRF token to POST, PUT, DELETE, and PATCH requests
 * - Reads token from cookie or meta tag
 * - Works with Angular's built-in XSRF protection
 *
 * Note: Backend must be configured to:
 * - Set CSRF token in cookie or meta tag
 * - Validate CSRF token on state-changing requests
 *
 * @example
 * ```typescript
 * // Token is automatically added to state-changing requests
 * this.http.post('/api/data', { value: 'test' }).subscribe();
 * // Request will include X-CSRF-TOKEN header
 * ```
 */

import { HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { CSRF_PROTECTED_METHODS, SECURITY_CONFIG } from '../constants/security.constants';

/**
 * CSRF Interceptor Function
 *
 * Functional interceptor that adds CSRF tokens to protected requests.
 * Uses Angular 15+ functional interceptor pattern.
 *
 * @param req - HTTP request
 * @param next - HTTP handler function
 * @returns Observable of HTTP event
 */
export const csrfInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
) => {
  // Only add CSRF token to state-changing requests
  if (!shouldAddCsrfToken(req)) {
    return next(req);
  }

  // Get CSRF token
  const token = getCsrfToken();

  // If no token available, proceed without it
  // (First request to get token won't have one)
  if (!token) {
    return next(req);
  }

  // Clone request and add CSRF token header
  const clonedReq = req.clone({
    setHeaders: {
      [SECURITY_CONFIG.CSRF_HEADER_NAME]: token
    }
  });

  return next(clonedReq);
};

/**
 * Determines if CSRF token should be added to the request
 *
 * CSRF protection is needed for:
 * - State-changing HTTP methods (POST, PUT, DELETE, PATCH)
 * - Same-origin requests only
 *
 * @param req - HTTP request to check
 * @returns True if CSRF token should be added
 */
function shouldAddCsrfToken(req: HttpRequest<any>): boolean {
  // Check if method requires CSRF protection
  const methodRequiresToken = CSRF_PROTECTED_METHODS.includes(
    req.method as any
  );

  if (!methodRequiresToken) {
    return false;
  }

  // Only add CSRF token to same-origin requests
  // External API calls should not include CSRF tokens
  return isSameOrigin(req.url);
}

/**
 * Checks if URL is same-origin
 *
 * @param url - URL to check
 * @returns True if URL is same-origin or relative
 */
function isSameOrigin(url: string): boolean {
  // Relative URLs are same-origin
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return true;
  }

  // Compare origin
  try {
    const requestUrl = new URL(url);
    const currentOrigin = window.location.origin;
    return requestUrl.origin === currentOrigin;
  } catch {
    // If URL parsing fails, assume same-origin for safety
    return true;
  }
}

/**
 * Gets CSRF token from cookie or meta tag
 *
 * Tries multiple sources in order:
 * 1. Cookie (Angular's default XSRF token cookie)
 * 2. Meta tag in HTML head
 *
 * @returns CSRF token or null if not found
 */
function getCsrfToken(): string | null {
  // Try to get token from cookie first
  const tokenFromCookie = getCookieValue(SECURITY_CONFIG.CSRF_COOKIE_NAME);
  if (tokenFromCookie) {
    return tokenFromCookie;
  }

  // Fallback to meta tag
  const tokenFromMeta = getMetaTagValue('csrf-token');
  if (tokenFromMeta) {
    return tokenFromMeta;
  }

  return null;
}

/**
 * Gets value from cookie by name
 *
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
function getCookieValue(name: string): string | null {
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.split('=').map(c => c.trim());
    if (cookieName === name) {
      return decodeURIComponent(cookieValue);
    }
  }

  return null;
}

/**
 * Gets content attribute value from meta tag
 *
 * @param name - Meta tag name
 * @returns Meta tag content value or null if not found
 */
function getMetaTagValue(name: string): string | null {
  const metaTag = document.querySelector(`meta[name="${name}"]`);
  return metaTag?.getAttribute('content') || null;
}
